import React, { useState, useEffect } from 'react';
import { AtSign, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Keeping the utility functions unchanged
const AUTH_TIMEOUT = 24 * 60 * 60 * 1000;

const setUserWithExpiry = (userData) => {
  const now = new Date().getTime();
  const item = {
    value: userData,
    expiry: now + AUTH_TIMEOUT,
  };
  localStorage.setItem('user', JSON.stringify(item));
};

const checkAndClearExpiredAuth = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;

  const item = JSON.parse(userStr);
  const now = new Date().getTime();

  if (now > item.expiry) {
    localStorage.removeItem('user');
    window.location.href = '/login';
    return null;
  }
  return item.value;
};

const logout = () => {
  localStorage.removeItem('user');
  window.location.href = '/login';
  window.location.reload();
};

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [notification, setNotification] = useState({
    show: false,
    success: false,
    message: ''
  });
  const [forgotPassword, setForgotPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState({
    show: false,
    success: false,
    message: ''
  });

  useEffect(() => {
    checkAndClearExpiredAuth();
    const interval = setInterval(checkAndClearExpiredAuth, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const user = localStorage.getItem('user');
    console.log(user);
    if (user) {
      window.location.href = '/';
    }
  }, []);

  // Keeping all the handler functions unchanged
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoginError('');
  
    if (!validateForm()) {
      // Display form validation errors directly in the UI
      if (errors.email) {
        setLoginStatus({
          show: true,
          success: false,
          message: errors.email
        });
      } else if (errors.password) {
        setLoginStatus({
          show: true,
          success: false,
          message: errors.password
        });
      }
      
      setTimeout(() => {
        setLoginStatus(prev => ({ ...prev, show: false }));
      }, 3000);
      
      return;
    }
    
    // Keeping the fetch logic unchanged
    fetch(`${process.env.REACT_APP_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: formData.email,
        password: formData.password,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('Invalid email or password');
          } else if (response.status === 404) {
            throw new Error('Server unavailable');
          } else {
            throw new Error('Something went wrong. Please try again later.');
          }
        }
        return response.json();
      })
      .then((data) => {
        console.log('Login data:', data);
        setUserWithExpiry({
          id: data.user.id || '',
          email: data.user.email || '',
          firstName: data.user.firstName || '',
          lastName: data.user.lastName || '',
          phone: data.user.phoneNumber || '',
          token: data.token || ''
        });

        setLoginStatus({
          show: true,
          success: true,
          message: 'Login successful!'
        });

        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      })
      .catch((error) => {
        console.error('Error during login:', error.message);
        setLoginStatus({
          show: true,
          success: false,
          message: error.message || 'Login failed. Please try again.'
        });

        setTimeout(() => {
          setLoginStatus(prev => ({ ...prev, show: false }));
        }, 3000);
      });
  };

  const handleResetLink = (e) => {
    e.preventDefault();
  
    setErrors({});
    setNotification({ show: false, success: false, message: "" });

    if (!formData.email.trim()) {
      setErrors({ email: "Email is required" });
      setLoginStatus({
        show: true,
        success: false,
        message: "Email is required"
      });
      
      setTimeout(() => {
        setLoginStatus(prev => ({ ...prev, show: false }));
      }, 3000);
      return;
    }
  
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrors({ email: "Invalid email format" });
      setLoginStatus({
        show: true,
        success: false,
        message: "Invalid email format"
      });
      
      setTimeout(() => {
        setLoginStatus(prev => ({ ...prev, show: false }));
      }, 3000);
      return;
    }

    fetch("https://atabackend.quindl.com/auth/send-reset-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: formData.email }),
    })  
      .then((response) => {
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Server unavailable');
          } else {
            throw new Error('Something went wrong. Please try again later.');
          }
        }
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          setNotification({
            show: true,
            success: true,
            message: "Reset link sent successfully. Check your email.",
          });
          setLoginStatus({
            show: true,
            success: true,
            message: "Reset link sent successfully. Check your email."
          });
        } else {
          throw new Error(data.message || "Failed to send reset link.");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setNotification({
          show: true,
          success: false,
          message: error.message || "Failed to send reset link.",
        });
        setLoginStatus({
          show: true,
          success: false,
          message: error.message || "Failed to send reset link."
        });
      });
      
    setTimeout(() => {
      setLoginStatus(prev => ({ ...prev, show: false }));
    }, 3000);
  };
  
  return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <AnimatePresence>
        {loginStatus.show && (
          <motion.div 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 z-50 px-6 py-4 rounded-lg shadow-lg text-center w-72 mx-auto left-0 right-0 
              ${loginStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
          >
            <div className="flex items-center justify-center">
              {loginStatus.success ? (
                <CheckCircle className="mr-2 h-6 w-6" />
              ) : (
                <AlertCircle className="mr-2 h-6 w-6" />
              )}
              {loginStatus.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full max-w-md mx-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
        >
          {!forgotPassword ? (
            <div className="p-8">
              <h2 className="text-3xl font-bold mb-2 text-center text-gray-800">
                Welcome Back
              </h2>
              <p className='text-center font-semibold mb-2 text-center text-gray-800 '>Hope you're feeling good today</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="Enter your email"
                    />
                    <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  </div>
                  {errors.email && (
                    <motion.p 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="Enter your password"
                    />
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <motion.p 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.password}
                    </motion.p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setForgotPassword(true)}
                    className="text-sm font-medium text-orange-600 hover:text-orange-500 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 px-4 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  Sign In
                </motion.button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <a 
                    href="/register"
                    className="font-medium text-orange-600 hover:text-orange-500 transition-colors"
                  >
                    Register
                  </a>
                </p>
              </div>
            </div>
          ) : (
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
                Reset Password
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="Enter your email"
                    />
                    <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  </div>
                  {errors.email && (
                    <motion.p 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </div>

                <motion.button
                  onClick={handleResetLink}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 px-4 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  Send Reset Link
                </motion.button>

                <button
                  onClick={() => setForgotPassword(false)}
                  className="w-full text-sm font-medium text-orange-600 hover:text-orange-500 transition-colors"
                >
                  Back to login
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;